import { LinkButton, SingleSelect, Tooltip } from '@pmi/dsm-react';
import { useSelector } from 'react-redux';
import { useState } from 'react';
import { adminUsersLayoutSelector } from '../adminUsersLayoutSlice';
import styles from './BulkActionMenu.module.scss';
import { User } from '../../../../../shared/models/UserModels';
import { usersResendList } from '../../resendActionSlice';
import ResendInvitesAction from './ResendInvitesAction';
import DisableAccessAction from './DisableAccessAction';
import usePermissions from '../../../../../shared/hooks/usePermissions';
import PERMISSIONS from '../../../../../shared/permissions';
import InviteWizard from '../../../../../shared/components/InviteWizard';

const options = [
  {
    label: 'Add products',
    value: 'add_product',
    disabled: false,
    disabledTooltip: 'This action is not supported for these users.',
  },
  {
    label: 'Unassign Products',
    value: 'remove_product',
    disabled: false,
    disabledTooltip: 'This action is not supported for these users.',
  },
  {
    label: 'Disable Access',
    value: 'disable_access',
    disabled: false,
    disabledTooltip: 'This action is not supported for these users.',
  },
  {
    label: 'Enable Access',
    value: 'enable_access',
    disabled: false,
    disabledTooltip: 'This action is not supported for these users.',
  },
  {
    label: 'Resend Invites',
    value: 'resend_invites',
    disabled: false,
    disabledTooltip: 'This action is not supported for these users.',
  },
];

function getOptions(
  selectedUsers: User[],
  resendList: string[],
  hasAccess: (args: any) => boolean,
  restrictedAgreementIds: string[],
) {
  const removedCount = selectedUsers.filter((u) => !u.isRemoved).length;
  const hasDisablePermission = hasAccess({
    permissions: [PERMISSIONS.ManageDealUsers],
  });
  const canUnassignProducts = hasAccess({
    permissions: [PERMISSIONS.UnassignProducts],
  });

  const restrictedIdSet = new Set(restrictedAgreementIds);
  const hasRestrictedUsers = selectedUsers.some(
    (u) =>
      !u.isRemoved &&
      u.assignedProducts.length > 0 &&
      u.deals.some((d) => restrictedIdSet.has(d.dealId)),
  );

  const disableAccessOption = options.find((i) => i.value === 'disable_access');
  disableAccessOption.disabled =
    removedCount === 0 || !hasDisablePermission || hasRestrictedUsers;
  disableAccessOption.disabledTooltip =
    'This action is not supported for these users.';

  const resendCount = selectedUsers.filter(
    (u) =>
      !u.isRemoved &&
      u.invitationStatus?.toLowerCase() === 'pending' &&
      !resendList.includes(u.email),
  ).length;
  options.find((i) => i.value === 'resend_invites').disabled =
    resendCount === 0;

  const restoreCount = selectedUsers.filter((u) => u.isRemoved).length;
  options.find((i) => i.value === 'enable_access').disabled =
    restoreCount === 0;

  options.find((i) => i.value === 'add_product').disabled = restoreCount !== 0;
  options.find((i) => i.value === 'remove_product').disabled =
    !canUnassignProducts || restoreCount !== 0;

  return options;
}

export default function BulkActionMenu({ userList }: { userList: User[] }) {
  const resendList = useSelector(usersResendList);
  const { hasAccess, restrictedAgreementIds } = usePermissions();
  const { selectedRows } = useSelector(adminUsersLayoutSelector);
  const selectedUserIds = selectedRows.map((e) => e.id);
  const selectedUsers =
    userList?.filter((user) => selectedUserIds.includes(user.id)) ?? [];

  const [selectedAction, setSelectedAction] = useState<string>();

  if (selectedUsers?.length <= 1) return null;

  return (
    <>
      <SingleSelect
        className={`ms-0 ms-sm-4 d-block ${styles.menu}`}
        label="Page Actions"
        placement="bottom"
        variant="primary"
        fontSize="md"
        isDropdownOnMobile={false}
        onSelect={(key) => {
          setSelectedAction(null);
          setSelectedAction(key);
        }}
        fontWeight="bold"
        withIconLabel
        type="text"
        options={[]}
      >
        <div className="d-flex flex-column">
          {getOptions(
            selectedUsers,
            resendList,
            hasAccess,
            restrictedAgreementIds,
          ).map((opt) => (
            <Tooltip
              content={opt.disabledTooltip}
              type="sm"
              placement="top"
              blockTriggers={!opt.disabled}
              key={opt.value}
              className="darkTooltip"
            >
              <LinkButton
                disabled={opt.disabled}
                className={`menu-item text-decoration-none text-muted ${
                  opt.disabled ? 'opacity-50' : ''
                }`}
                onClick={() => {
                  if (!opt.disabled) {
                    setSelectedAction(null);
                    setSelectedAction(opt.value);
                  }
                }}
                titleText={opt.label}
              />
            </Tooltip>
          ))}
        </div>
      </SingleSelect>
      {selectedAction === 'add_product' && (
        <InviteWizard
          people={selectedUsers}
          onCancel={() => setSelectedAction(null)}
        />
      )}
      {selectedAction === 'remove_product' && (
        <InviteWizard
          people={selectedUsers}
          onCancel={() => setSelectedAction(null)}
          action="remove"
        />
      )}
      {selectedAction === 'resend_invites' && (
        <ResendInvitesAction
          selectedUsers={selectedUsers}
          onActionComplete={() => setSelectedAction(null)}
        />
      )}
      {selectedAction === 'disable_access' && (
        <DisableAccessAction
          selectedUsers={selectedUsers}
          onActionComplete={() => setSelectedAction(null)}
        />
      )}
      {selectedAction === 'enable_access' && (
        <InviteWizard
          people={selectedUsers}
          onCancel={() => setSelectedAction(null)}
          action="restoreBulk"
        />
      )}
    </>
  );
}
